import { useEffect, useState } from "react";
import { clipDateYear } from "./dateUtils";

/** ISO "YYYY-MM-DD" → "DD.MM.YYYY" für die Anzeige. */
const isoToDe = (iso) => {
  if (!iso || typeof iso !== "string") return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[3]}.${m[2]}.${m[1]}`;
};

/** "DD.MM.YYYY" → ISO "YYYY-MM-DD" oder Leerstring wenn ungültig. */
const deToIso = (de) => {
  const m = (de || "").match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return "";
  const dd = m[1].padStart(2, "0");
  const mm = m[2].padStart(2, "0");
  const yyyy = m[3];
  const d = Number(dd), mo = Number(mm), y = Number(yyyy);
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || y < 1900 || y > 9999) return "";
  return `${yyyy}-${mm}-${dd}`;
};

/** Punkte beim Tippen automatisch einfügen → DDMMYYYY → DD.MM.YYYY. */
const autoFormat = (raw) => {
  const digits = (raw || "").replace(/[^\d]/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
};

/**
 * Geburtsdatum-Eingabe — funktioniert auf Desktop UND iPad/Safari.
 *
 * Verhalten:
 *  - Text-Input mit deutschem Format DD.MM.JJJJ
 *  - Punkte werden beim Tippen automatisch gesetzt (DDMMYYYY → DD.MM.YYYY)
 *  - "×"-Button rechts löscht den Wert vollständig
 *  - manuelle Eingabe ist auf allen Geräten möglich
 *  - intern wird ISO (YYYY-MM-DD) im Form-State gespeichert
 *
 * Props:
 *  - id, label, className, required
 *  - value: ISO-String oder ""
 *  - onChange: (iso) => void  — wird nur mit gültigen ISO-Werten (oder "")
 *    aufgerufen; während partieller Eingabe bleibt der Form-State unverändert
 */
export const DateField = ({
  id,
  label,
  value,
  onChange,
  className = "",
  required = false,
}) => {
  // Lokaler View-State: zeigt entweder die formatierte ISO-Eingabe oder einen
  // partiell getippten Wert, ohne dass dieser den Form-State "verschmutzt".
  const [text, setText] = useState(isoToDe(value));

  // Wenn der externe Wert sich ändert (z. B. Reset), Anzeige neu syncen.
  useEffect(() => {
    const next = isoToDe(value);
    setText((curr) =>
      // Vermeide unnötiges Überschreiben, wenn der User noch partiell tippt
      // und das resultierende ISO bereits dem aktuellen value entspricht.
      deToIso(curr) === value && next ? curr : next
    );
  }, [value]);

  const handleChange = (e) => {
    const formatted = autoFormat(e.target.value);
    setText(formatted);
    if (formatted === "") {
      onChange("");
      return;
    }
    const iso = deToIso(formatted);
    if (iso) onChange(clipDateYear(iso));
    // Bei Teil-Eingaben: nichts an onChange senden (Form-State bleibt leer)
  };

  const clear = () => {
    setText("");
    onChange("");
  };

  return (
    <label className={`block ${className}`}>
      <span className="eyebrow text-[#505a74]">
        {label}
        {required && " *"}
      </span>
      <div className="mt-2 relative">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="bday"
          data-testid={`form-input-${id}`}
          value={text}
          onChange={handleChange}
          placeholder="TT.MM.JJJJ"
          maxLength={10}
          className="block w-full bg-white border border-[#e8e6df] rounded-sm pl-4 pr-10 py-3 text-[15px] focus:outline-none focus:border-[#0a1128] transition"
        />
        {text && (
          <button
            type="button"
            aria-label="Datum löschen"
            data-testid={`form-input-${id}-clear`}
            onClick={clear}
            className="absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 inline-flex items-center justify-center rounded-full text-[#505a74] hover:bg-[#f4f3ed] hover:text-[#0a1128] transition"
          >
            <span aria-hidden="true" className="text-[18px] leading-none">
              ×
            </span>
          </button>
        )}
      </div>
    </label>
  );
};
